import { areTimeRangeInputValuesEqual, type TimeRangeInputValue } from '@chronosphereio/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePinnedTimeRange, usePinnedTimeRangeStatus } from './pinned-time-range-hooks';
import { type TimeRangeProviderOptions } from './time-range-api';
import { useTimeRangeQueryParamsState } from './url-query-params';
import { getEndParamName, getStartParamName } from './url-query-params-config';

export interface UrlTimeRangeStorage {
  timeRange: TimeRangeInputValue;
  setTimeRange: (next: TimeRangeInputValue, updateType: 'pushIn' | 'replaceIn') => void;
}

/**
 * Handles getting / settting the time range state in the URL.
 */
export function useUrlTimeRangeStorage(options: TimeRangeProviderOptions): UrlTimeRangeStorage {
  const startParamName = getStartParamName(options.urlParamsScope);
  const endParamName = getEndParamName(options.urlParamsScope);

  const { timeRangeInUrl, setTimeRangeInUrl } = useTimeRangeQueryParamsState(startParamName, endParamName);

  const { pinnedTimeRange } = usePinnedTimeRange();
  const { pinnedStatus } = usePinnedTimeRangeStatus();

  const [lastTimeRangeViaSetCall, setLastTimeRangeViaSetCall] = useState<TimeRangeInputValue>(() => {
    // It's possible there's nothing in the URL yet (i.e. timeRangeInUrl is undefined), so if that's the case,
    // use an initial value based on pinned status / options and then update the URL in the effect below
    let initialTimeRange: TimeRangeInputValue;
    if (timeRangeInUrl === undefined) {
      initialTimeRange =
        options.enablePinning && pinnedStatus === 'pinned' ? pinnedTimeRange : options.defaultTimeRange;
    } else {
      initialTimeRange = timeRangeInUrl;
    }
    return initialTimeRange;
  });

  const setTimeRange = useCallback(
    (next: TimeRangeInputValue, updateType: 'pushIn' | 'replaceIn') => {
      // Keep track of time range values passed to set so we can maintain object identity below in the useMemo
      setLastTimeRangeViaSetCall(next);
      setTimeRangeInUrl(next, updateType);
    },
    [setTimeRangeInUrl, setLastTimeRangeViaSetCall]
  );

  const timeRange = useMemo(() => {
    // Initially if we don't have something in the URL, use the value we initialized since it'll get set in the URL
    // in the effect below
    if (timeRangeInUrl === undefined) {
      return lastTimeRangeViaSetCall;
    }

    // Maintain object identity for set calls by reusing the value stored in state if the URL matches
    if (areTimeRangeInputValuesEqual(timeRangeInUrl, lastTimeRangeViaSetCall)) {
      return lastTimeRangeViaSetCall;
    }

    // Otherwise, assume what's in the URL is right (e.g. if the browser back / forward buttons are used)
    return timeRangeInUrl;
  }, [timeRangeInUrl, lastTimeRangeViaSetCall]);

  // Have to do this with a useEffect (rather than setting during render) because the URL params state is not owned by
  // our component, but rather a query params / router provider at the top of the tree
  useEffect(() => {
    // NB: Only run this effect if URL params are enabled since it will modify the URL
    if (options.enableUrlParams === false) {
      return;
    }

    if (timeRangeInUrl !== undefined) {
      return;
    }

    setTimeRangeInUrl(lastTimeRangeViaSetCall, 'replaceIn');
  }, [options.enableUrlParams, timeRangeInUrl, setTimeRangeInUrl, lastTimeRangeViaSetCall]);

  return {
    timeRange,
    setTimeRange,
  };
}
